import { Serializable, EssentialObject } from '../../common/types'
import { UPCOMING_LIMIT_FACTOR } from './vehicle-maintenance-details.object'
import { isNumeric } from 'rxjs/internal-compatibility'
export interface PartInstanceFormData {
  part_number_name: string
  part_number_id: number
  serial: string
  creation_timestamp: string
  vehicle_id?: number
  vehicle_name?: string
}

type MaybeLimitation = number | null
export interface PartNumberForInstance {
  id: number
  title: string
  number: string
  life_limit_days: MaybeLimitation
  life_limit_flights: MaybeLimitation
  life_limit_flight_hours: MaybeLimitation
  maintenance_days: MaybeLimitation
  maintenance_flights: MaybeLimitation
  maintenance_flight_hours: MaybeLimitation
}

export interface PartInstanceUser {
  id: number
  name: string
}

export interface PartInstanceVehicle {
  id: number
  name: string
}

export interface PartInstanceLog {
  id: number
  action_type: string
  action_timestamp: string
  part_instance_id: number
  user: PartInstanceUser
  vehicle: PartInstanceVehicle
}

export interface PartLifeCycleInfo {
  creation_timestamp: string
  status: string
  user_name: string
  vehicle_name: string
}

export interface PartInstanceLog {
  action_type: string
  action_timestamp: string
  description: string
  id: number
  part_instance_id: number
  user: EssentialObject
  vehicle: EssentialObject
}

export interface PartLimitDisplayInfo {
  formattedLimit: string
  isLimitUpcoming: boolean
  isLimitReached: boolean
}

function getPartLifeLimitDisplayInfo(partInstanceValue: number, maybePartLimit: MaybeLimitation): PartLimitDisplayInfo {
  return {
    formattedLimit: formatCounterWithLimit(partInstanceValue, maybePartLimit),
    isLimitUpcoming: maybePartLimit !== null && partInstanceValue >= maybePartLimit * UPCOMING_LIMIT_FACTOR,
    isLimitReached: maybePartLimit !== null && partInstanceValue >= maybePartLimit,
  }
}

function getPartInspectionLimitDisplayInfo(
  partRemainingToInspectionValue: number,
  maybePartLimit: MaybeLimitation,
): PartLimitDisplayInfo {
  return {
    ...getPartLifeLimitDisplayInfo(maybePartLimit - partRemainingToInspectionValue, maybePartLimit),
    formattedLimit: getFormattedLimitation(partRemainingToInspectionValue),
  }
}

function getFormattedLimitation(limit: MaybeLimitation): string {
  return isNumeric(limit) ? limit.toString() : '--'
}

function formatCounterWithLimit(counter: number, limit: MaybeLimitation) {
  return `${counter}/${getFormattedLimitation(limit)}`
}
interface PartLimitsDisplayInfo {
  daysToInspection: PartLimitDisplayInfo
  flightCyclesToInspection: PartLimitDisplayInfo
  flightHoursToInspection: PartLimitDisplayInfo
  daysLifeLimit: PartLimitDisplayInfo
  flightCyclesLifeLimit: PartLimitDisplayInfo
  flightHoursLifeLimit: PartLimitDisplayInfo
}

export class PartInstance extends Serializable<PartInstance> {
  id: number
  serial: string
  vehicle_id: number | null = null
  days_in_use: number
  flight_hours: number
  flights: number
  flights_since_last_maintenance: number | null = null
  part_number: PartNumberForInstance | null = null
  last_install_log_record: PartInstanceLog | null = null
  life_cycle_info: PartLifeCycleInfo | null = null
  creation_timestamp: string | null = null
  last_maintenance_timestamp: string | null = null
  maintenance_in_days: number | null = null
  maintenance_in_flights: number | null = null
  maintenance_in_flight_hours: number | null = null
  health_status: string | null = null

  updatedTitle: string

  limitsDisplayInfo: PartLimitsDisplayInfo | null = null

  isUnusable: boolean

  constructor(details: PartInstanceResponsePayload) {
    super()
    this.checkAndDeserialize(details)
  }

  deserialize(details: PartInstanceResponsePayload): void {
    this.id = details['id']
    this.serial = details['serial']
    this.vehicle_id = details['vehicle_id'] || null
    this.days_in_use = details['days_in_use']
    this.flight_hours = details['flight_hours']
    this.flights = details['flights']
    this.flights_since_last_maintenance = details['flights_since_last_maintenance'] || 0
    this.last_install_log_record = details['last_install_log_record'] || null
    this.life_cycle_info = details['life_cycle_info'] || null
    this.part_number = details['part_number'] || null
    this.creation_timestamp = details['creation_timestamp'] || ''
    this.last_maintenance_timestamp = details['last_maintenance_timestamp'] || ''
    this.maintenance_in_days = details['maintenance_in_days']
    this.maintenance_in_flights = details['maintenance_in_flights']
    this.maintenance_in_flight_hours = details['maintenance_in_flight_hours']
    this.health_status = details['health_status'] || ''

    // Calculating the limits and "isUnusable" in helper variables instead of functions to prevent change detection from running the function many times.
    // All the properties are never changed after deserialize(), so these values should never change.
    // Unfortunately, can't mark them as `readonly`, although they really are.
    this.limitsDisplayInfo = {
      daysToInspection: getPartInspectionLimitDisplayInfo(this.maintenance_in_days, this.part_number.maintenance_days),
      flightCyclesToInspection: getPartInspectionLimitDisplayInfo(
        this.maintenance_in_flights,
        this.part_number.maintenance_flights,
      ),
      flightHoursToInspection: getPartInspectionLimitDisplayInfo(
        this.maintenance_in_flight_hours,
        this.part_number.maintenance_flight_hours,
      ),
      daysLifeLimit: getPartLifeLimitDisplayInfo(this.days_in_use, this.part_number.life_limit_days),
      flightCyclesLifeLimit: getPartLifeLimitDisplayInfo(this.flights, this.part_number.life_limit_flights),
      flightHoursLifeLimit: getPartLifeLimitDisplayInfo(this.flight_hours, this.part_number.life_limit_flight_hours),
    } as const
    this.isUnusable = Object.values(this.limitsDisplayInfo).some((limit) => limit.isLimitReached)
  }
}

export type PartInstanceResponsePayload = Partial<PartInstance>
